/* eslint-disable sonarjs/cognitive-complexity */
import {
  DirectSales,
  DirectSalesAsset,
  DirectSalesAssetLine,
  DirectSalesGrid,
  DirectSalesLine,
  DirectSalesLineAllocation,
  DirectSalesLineObjective,
  DirectSalesLockState,
  DirectSalesLockStatusOption,
  DirectSalesPricingMode,
  DirectSalesProductCategoryExclusivityType,
  DirectSalesState,
  DirectSalesStatusOption,
  DirectSalesTransientDealLineState,
  DirectSalesUpdatedCampaignNegotiationTypes,
} from 'components/common/types/DirectSalesCampaign.types';
import { PublisherCampaignConfiguration, PublisherType } from 'components/common/types/Publisher.types';
import {
  resolveDirectSalesLinePricingMode,
  resolveDirectSalesPricingModeForApi,
} from 'utils/resolvePublisherDefaultDirectSalesPricingMode';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isAfter } from 'date-fns';
import merge from 'lodash/merge';

import { CodeNameModel } from 'components/common/types';
import {
  AverageScoresByFrame,
  Segment,
  SegmentFrameAverageByHour,
  SegmentHourAverageByFrame,
  SelectedSegment,
} from 'components/common/types/Segment.types';
import {
  Advertiser,
  AuctionModel,
  Availability,
  Brand,
  CommonDeal,
  DealPriority,
  DealStatus,
  DealType,
  DistributionOfAvailabilityFilter,
  Dsp,
  FrontEndType,
  ImpressionMetrics,
  IndexOptimisation,
  Line,
  PreservedAvailability,
  Programmatic,
  SourceSystem,
} from 'components/common/types/Deal.types';

import { PermissionsEnum } from 'consts/permissions';
import Auth from 'modules/Auth';
import { CampaignType } from 'components/pages/Planner/PlannerSections/types';

import { Asset } from 'components/common/types/Asset.types';
import { last } from 'lodash';
import { DEFAULT_CRITERIA } from 'consts/targetCriteria';
import { CampaignVersioningEditingStatus, CampaignVersioningErrors } from 'consts/directSalesCampaignVersioning';
import { CampaignViolation } from 'consts/campaignViolations';
import { DirectSalesExclusionFlag } from 'components/common/types/DirectSalesDeal.types';
import { isDirectSalesCampaignCloned } from 'components/pages/Planner/hooks/utils/isDirectSalesCampaignCloned';
import { generateUniqueDealLineName } from 'components/pages/Planner/hooks/utils/createNewDirectSalesLine';
import { getAssetByFrameId, getDuplicateLineState, getLineById, getLineByIdOrLineId } from './reducerUtils';
import { defaultDirectSales, defaultLine } from './defaults';
import {
  AllowedStateTransitionsMetadata,
  AssetsView,
  BackupFormData,
  CampaignPlannerMode,
  CampaignPlannerModeType,
  DealManagement,
  DealManagementMeta,
  DisclaimerObjectives,
  Loading,
  LocalLoading,
  PlannerSecondaryPanelType,
  PlannerSettings,
} from './types';

export const getDefaultDealType = (publisherType?: PublisherType): DealType => {
  let dealType = DealType.NON_GUARANTEED_FLOOR_PRICE;

  if (publisherType === PublisherType.RESELLER || !publisherType) return dealType;

  if (Auth.hasPermission(PermissionsEnum.DEAL_TYPE_PRICE_VIEW)) {
    dealType = DealType.NON_GUARANTEED_FIXED_PRICE;
  } else if (Auth.hasPermission(PermissionsEnum.DEAL_TYPE_PRICEIMPRESSION_VIEW)) {
    dealType = DealType.GUARANTEED;
  }

  return dealType;
};

const defaultResellerEntityValues = {
  code: 'RESELLER',
  name: 'RESELLER',
};

const defaultResellerFormValues = {
  advertiser: defaultResellerEntityValues,
  brand: defaultResellerEntityValues,
  productCategory: defaultResellerEntityValues,
};

export const initialState: DealManagement = {
  backupFormData: {
    commonDeal: {} as unknown as CommonDeal,
    programmatic: {} as unknown as Programmatic,
    lines: [],
  },
  programmatic: {
    dsp: null as unknown as Dsp,
    dspIsLegacy: false,
    syncWithDsp: false,
    dspSeatId: '',
    enableCreativeSubmissionInBidStream: false,
    enableOpenMarketplace: false,
    enableLineLevelTrading: false,
  },
  directSales: defaultDirectSales,
  directSalesSessionLock: {
    status: DirectSalesLockStatusOption.UNLOCKED,
    expires: '',
    owner: '',
  },
  commonDeal: {
    advertiser: null as unknown as Advertiser,
    advertiserBlockExclusion: DirectSalesExclusionFlag.ON,
    agencyBlockExclusion: DirectSalesExclusionFlag.ON,
    campaignStatus: DirectSalesStatusOption.DRAFT_UPDATING,
    auctionModel: AuctionModel.FIRST_PRICE,
    bookingStatusCode: '' as DealStatus,
    brand: null as unknown as Brand,
    cpm: '',
    dealId: '',
    dealName: '',
    dealPriority: DealPriority.STANDARD,
    dealType: getDefaultDealType(),
    duplicationExclusion: DirectSalesExclusionFlag.ON,
    exclusions: {
      organisations: [],
      productCategories: [],
    },
    externalReference: '',
    internalId: '',
    impressionMetrics: undefined,
    juxtapositionExclusion: DirectSalesExclusionFlag.ON,
    overriddenCampaignId: '',
    pricingSolution: { grid: [], gridNegotiation: { defaultValue: '', values: [] } },
    productCategory: null as unknown as CodeNameModel,
    productCategoryExclusivity: DirectSalesProductCategoryExclusivityType.OFF,
    specialist: null as unknown as CodeNameModel,
    salesPerson: null as unknown as CodeNameModel,
    salesTeam: null as unknown as CodeNameModel,
    specialistBlockExclusion: DirectSalesExclusionFlag.ON,
    adminPerson: null as unknown as CodeNameModel,
    agency: null as unknown as CodeNameModel,
    sourceSystem: '' as SourceSystem,
    summary: {
      adjustedSots: [],
      availability: {
        allocatedImpressions: 0,
        availableImpressions: 0,
        allocatedFrames: 0,
        assetCpmDistribution: {
          max: 0,
          min: 0,
          weightedMean: 0,
        },
        assets: [],
        availableFrames: 0,
        totalCost: 0,
      },
      budget: 0,
      cities: [],
      counties: [],
      countries: [],
      deliveredImpressions: 0,
      endDate: null,
      environments: [],
      frames: 0,
      impressions: 0,
      listFiles: [],
      maxTargetSot: 0,
      minTargetSot: 0,
      postCodes: [],
      productFormats: [],
      routeFrameCodes: [],
      sot: [],
      startDate: null,
      streets: [],
      sweeps: [],
      tags: [],
    },
    currentLine: defaultLine,
    expirationDate: null as unknown as Date,
    bids: {
      bidRequests: 0,
      bidResponses: 0,
      bookedImpressions: 0,
      fillRate: 0,
      revenue: 0,
      soldImpressions: 0,
      wonImpressions: 0,
    },
  },
  isOOCFramesModalDismissed: false,
  isActionsDisabled: false,
  isDisclaimerVisible: false,
  isDuplicated: false,
  isEditingDisabled: false,
  isLoading: false,
  isFetchingAllocation: false,
  isFetchingPricing: false,
  isFetchingReferences: false,
  isFetchingNetworks: false,
  isFetchingAdvancedAvailabilityReport: false,
  isFetchingProximityReport: false,
  loading: {},
  isForecastedAllocation: false,
  hasUnsavedProposal: false,
  isNewDeal: false,
  isCpmCampaignLevel: true,
  plannerSettings: {
    assetsView: 'AVAILABLE',
    isImpressionsClusterVisible: false,
    isAvailabilityClusterVisible: false,
    isAllocationClusterVisible: false,
    impressions: {
      min: 0,
      max: 0,
    },
    enabledImpressionsRange: [],
    assetsOnViewport: [],
    isAvailabilityClusterToggleDisabled: false,
    isAllocationClusterToggleDisabled: false,
    zoom: 0,
    frameIdIndexes: [],
    selectedIndexRange: undefined,
    isSummaryPanelOpen: false,
    isSimplifiedPlanningMode: false,
    dealLineSectionOpenState: {
      info: false,
      filters: false,
      targets: false,
      CPMPricing: false,
    },
    isClientSideAssetFiltering: false,
    filteredAssetsIds: {},
    hoveredFurnitureCode: '',
    isFullAvailabilityRequested: false,
  },
  meta: {
    poi: {
      attributes: [],
      datasources: [],
    },
    productCategoryGroups: [],
    allowedStateTransitions: {},
    originalLineStatuses: {},
  },
  temporaryDealId: '',
  disclaimer: {
    success: [],
    warning: [],
    danger: [],
  },
  segmentFrameAverageByHour: {
    segmentValues: [],
    avgScoresByHour: [],
  },
  selectedIndexOptimisationDataProvider: '',
  plannerSecondaryPanelType: PlannerSecondaryPanelType.NONE,
  campaignType: CampaignType.PROGRAMMATIC,
  isCampaignDrawerOpen: false,
  campaignViolations: [],
  isCampaignNameFocusRequested: false,
  campaignPlannerMode: CampaignPlannerMode.MAP,
  isDspNoLongerInUseDialogVisible: false,
};

export const dealManagementSlice = createSlice({
  name: 'dealManagement',
  initialState,
  reducers: {
    updatePlannerIndexOptimisation: (state, action: PayloadAction<IndexOptimisation[]>) => {
      state.commonDeal.currentLine.indexOptimisation = action.payload;
    },
    dismissOOCFramesModal: (state) => {
      state.isOOCFramesModalDismissed = true;
    },
    changeCurrentLine: (state, action: PayloadAction<string>) => {
      const chosenLine = getLineById(state.backupFormData.lines, action.payload);

      state.commonDeal.currentLine = chosenLine;
      state.isOOCFramesModalDismissed = false;
      state.plannerSettings.isFullAvailabilityRequested = false;
    },
    changeCurrentLineToLastLine: (state) => {
      const lastLine = last(state.backupFormData.lines);

      if (lastLine) {
        state.commonDeal.currentLine = lastLine;
        state.isOOCFramesModalDismissed = false;
        state.plannerSettings.isFullAvailabilityRequested = false;
      }
    },
    changeCurrentLineAvailability: (state, action: PayloadAction<Partial<Availability>>) => {
      state.commonDeal.currentLine.availability = {
        ...state.commonDeal.currentLine.availability,
        ...action.payload,
      };
    },
    changeDealCurrentLineData: (state, action: PayloadAction<Partial<Line | DirectSalesLine>>) => {
      state.commonDeal.currentLine = { ...state.commonDeal.currentLine, ...action.payload };
    },
    changeDealCurrentLineAvailabilityAssetsSot: (state, action: PayloadAction<Pick<Asset, 'frameId' | 'sot'>[]>) => {
      state.commonDeal.currentLine.availability.assets = state.commonDeal.currentLine.availability.assets.map(
        (currentLineAvailabilityAsset: Asset) => {
          const assetInPayload = action.payload.find(
            (payloadAsset) => payloadAsset.frameId === currentLineAvailabilityAsset.frameId,
          );

          return {
            ...currentLineAvailabilityAsset,
            sot: assetInPayload?.sot ?? currentLineAvailabilityAsset.sot,
          };
        },
      );
    },
    changeDealData: (state, action: PayloadAction<Partial<CommonDeal>>) => {
      state.commonDeal = { ...state.commonDeal, ...action.payload };
    },
    changeOnlyProgrammaticDealData: (state, action: PayloadAction<Partial<Programmatic>>) => {
      state.programmatic = { ...state.programmatic, ...action.payload };
    },
    changeDirectSalesNegotiationFailedValues: (state, action: PayloadAction<string[]>) => {
      state.directSales.failedGridNegotiationTypes = action.payload;
    },
    changeDealDetails: (
      state,
      action: PayloadAction<{
        backupFormData: BackupFormData;
        commonDeal: Partial<CommonDeal>;
        hasAdsDealLevelCPMEnabled: boolean;
        preferredLineId?: string;
      }>,
    ) => {
      const backupLines = action.payload.backupFormData.lines.map((line: Line) =>
        merge({}, { ...initialState.commonDeal.currentLine }, line),
      );

      const cpm = action.payload.hasAdsDealLevelCPMEnabled
        ? action.payload.commonDeal.currentLine?.cpm || backupLines[0].cpm
        : action.payload.commonDeal.cpm || '';

      const programmaticData = { ...initialState.programmatic, ...action.payload.backupFormData.programmatic };

      const backupFormData = {
        commonDeal: merge(
          {},
          { ...initialState.commonDeal },
          {
            ...action.payload.backupFormData.commonDeal,
            cpm,
          },
        ),
        programmatic: programmaticData,
        lines: backupLines,
      };

      const commonDeal = merge({}, { ...initialState.commonDeal }, { ...action.payload.commonDeal });
      const lineIdToUse = action.payload.preferredLineId || state.commonDeal.currentLine.id;
      const currentLineFromBackup =
        getLineByIdOrLineId(action.payload.backupFormData.lines, lineIdToUse) || action.payload.commonDeal.currentLine;
      const currentLine = merge({}, { ...initialState.commonDeal.currentLine }, { ...currentLineFromBackup });

      const previousAvailability = state.commonDeal.currentLine.availability;

      currentLine.availability = {
        ...currentLine.availability,
        totalFrames: currentLine.availability.totalFrames ?? previousAvailability.totalFrames,
        maxAvailableImpressions:
          currentLine.availability.maxAvailableImpressions ?? previousAvailability.maxAvailableImpressions,
        universeFrames: currentLine.availability.universeFrames ?? previousAvailability.universeFrames,
        universeImpressions: currentLine.availability.universeImpressions ?? previousAvailability.universeImpressions,
      };

      state.isNewDeal = initialState.isNewDeal;
      state.commonDeal = { ...commonDeal, currentLine, cpm };
      state.programmatic = { ...programmaticData };
      state.backupFormData = { ...backupFormData };
      state.plannerSettings = {
        ...state.plannerSettings,
      };

      const directSales = {
        state: currentLine.state || initialState.directSales.state,
        lockState: currentLine.lockState || initialState.directSales.lockState,
        dealLineFormats: currentLine.dealLineFormats || [],
        locations: currentLine.locations || [],
        poi: currentLine.poi || [],
        poiPoints: state.directSales.poiPoints || [],
        mediaType: currentLine.mediaType,
        pricingMode: currentLine.pricingMode || DirectSalesPricingMode.CPM,
        allocation: currentLine.allocation || initialState.directSales.allocation,
        budgetCriteria: DEFAULT_CRITERIA,
        framesCriteria: DEFAULT_CRITERIA,
        impressionsCriteria: DEFAULT_CRITERIA,
        networkCriteria: [],
        objectives: currentLine.objectives || initialState.directSales.objectives,
        productTemplate: state.directSales.productTemplate || initialState.directSales.productTemplate,
        floatingRange: state.directSales.floatingRange || initialState.directSales.floatingRange,
        updatedGridNegotiationTypes: state.directSales.updatedGridNegotiationTypes,
      };

      state.directSales = directSales;

      const linesShareSameCpm =
        backupLines.length <= 1 || backupLines.every((line) => Number(line.cpm) === Number(backupLines[0].cpm));

      state.isCpmCampaignLevel = linesShareSameCpm;
    },
    changeCurrentDealLineAvailability: (state, action: PayloadAction<{ assets: Asset[] | DirectSalesAsset[] }>) => {
      const assets: Asset[] = action.payload.assets.map((asset: Asset) => {
        asset.mediaOwner = asset.mediaOwnerAssetId?.mediaOwner;
        asset.frameId = asset.mediaOwnerAssetId ? asset.mediaOwnerAssetId.assetId : asset.frameId;
        return {
          ...asset,
          street: asset.street ?? asset.address,
          postCode: asset.postCode ?? asset.postcode,
          availableImpressions: asset.impressions,
        };
      });

      state.commonDeal.currentLine.availability = {
        ...action.payload,
        assets,
      } as Availability;
      state.commonDeal.currentLine.preservedAvailability = {
        ...action.payload,
        assets: undefined,
      } as unknown as PreservedAvailability;
    },
    changeCurrentLineDistributionOfAvailability: (
      state,
      action: PayloadAction<Partial<DistributionOfAvailabilityFilter>>,
    ) => {
      state.commonDeal.currentLine.distributionOfAvailabilityFilter = {
        ...state.commonDeal.currentLine.distributionOfAvailabilityFilter,
        ...action.payload,
      };
    },
    changeBackupDealLineAvailabilityWithoutAssets: (
      state,
      action: PayloadAction<{ lineId: string; availability: Availability & PreservedAvailability }>,
    ) => {
      const idx = state.backupFormData.lines?.findIndex((line) => line.lineId === action.payload.lineId);
      const isLineInBackupLines = idx > -1;

      if (!isLineInBackupLines) {
        return;
      }

      if (isLineInBackupLines) {
        state.backupFormData.lines[idx] = {
          ...state.backupFormData.lines[idx],
          availability: { ...action.payload.availability, assets: [], missingAssets: [] },
          preservedAvailability: { ...action.payload.availability, assets: undefined } as PreservedAvailability,
        };
      }
    },
    changeDealLineAllocatedImpressions: (state, action: PayloadAction<Availability & PreservedAvailability>) => {
      const assets: Asset[] = action.payload.assets.map((asset: Asset) => ({
        ...(getAssetByFrameId(state.commonDeal.currentLine.availability.assets, asset.frameId) ?? asset),
        impressions: asset.impressions,
        allocatedImpressions: asset.impressions,
      }));

      state.commonDeal.currentLine.availability = { ...action.payload, assets };
      state.commonDeal.currentLine.preservedAvailability = {
        ...action.payload,
        assets: undefined,
      } as PreservedAvailability;
    },
    changeDealLineVenueTaxonomy: (state, action: PayloadAction<number[]>) => {
      state.commonDeal.currentLine.venueTaxonomies = action.payload;
    },
    addDealLineVenueTaxonomy: (state, action: PayloadAction<number>) => {
      state.commonDeal.currentLine.venueTaxonomies = [...state.commonDeal.currentLine.venueTaxonomies, action.payload];
    },
    removeDealLineVenueTaxonomy: (state, action: PayloadAction<number>) => {
      state.commonDeal.currentLine.venueTaxonomies = [
        ...state.commonDeal.currentLine.venueTaxonomies.filter(
          (venueTaxonomyCode) => venueTaxonomyCode !== action.payload,
        ),
      ];
    },
    changeSegmentFrameAverageByHour: (state, action: PayloadAction<SegmentFrameAverageByHour>) => {
      state.segmentFrameAverageByHour = action.payload;
    },
    clearSegmentFrameAverageByHour: (state) => {
      state.segmentFrameAverageByHour = initialState.segmentFrameAverageByHour;
    },
    changeIsCpmCampaignLevel: (state, action: PayloadAction<boolean>) => {
      state.isCpmCampaignLevel = action.payload;

      if (action.payload && !Number(state.commonDeal.cpm)) {
        state.commonDeal.cpm = state.commonDeal.currentLine.cpm ?? 0;
      }
    },
    changeDisclaimer: (state, action: PayloadAction<DisclaimerObjectives>) => {
      state.disclaimer = action.payload;
    },
    changeFormParams: (state, action: PayloadAction<Partial<DealManagement>>) => {
      return { ...state, ...action.payload };
    },
    changeFrontEndType: (state, action: PayloadAction<FrontEndType>) => {
      return {
        ...state,
        commonDeal: {
          ...state.commonDeal,
          currentLine: {
            ...state.commonDeal.currentLine,
            frontEndType: action.payload,
          },
        },
      };
    },
    changeIsNewDeal: (state, action: PayloadAction<PublisherType>) => {
      return {
        ...state,
        isNewDeal: true,
        isCampaignDrawerOpen: true,
        isEditingDisabled: false,
        isLoading: false,
        isFetchingReferences: false,
        isFetchingNetworks: false,
        isActionsDisabled: false,
        commonDeal: {
          ...state.commonDeal,
          dealType: getDefaultDealType(action.payload),
          ...(action.payload === PublisherType.RESELLER ? defaultResellerFormValues : null),
        },
      };
    },
    changeIsNewLineInEndedDeal: (state) => {
      return {
        ...state,
        isNewDeal: false,
        isEditingDisabled: false,
        isLoading: false,
        isFetchingReferences: false,
        isFetchingNetworks: false,
        isActionsDisabled: false,
      };
    },
    changeMeta: (state, action: PayloadAction<DealManagementMeta>) => {
      state.meta = {
        ...state.meta,
        ...action.payload,
      };
    },
    changeSegment: (state, action: PayloadAction<Partial<Segment>>) => {
      state.commonDeal.currentLine.segment = {
        ...state.commonDeal.currentLine.segment,
        ...action.payload,
      };
    },
    changeCampaignType: (state, action: PayloadAction<CampaignType>) => {
      state.campaignType = action.payload;
    },
    clearAllocationInputs: (state) => {
      state.commonDeal.currentLine = {
        ...state.commonDeal.currentLine,
        impressions: undefined,
        budget: undefined,
        sot: undefined,
        frames: undefined,
      };
    },
    clearDealLineVenueTaxonomies: (state) => {
      state.commonDeal.currentLine.venueTaxonomies = [];
    },
    clearSegment: (state) => {
      state.commonDeal.currentLine.segment.selectedSegments = {};
    },
    clearDisclaimer: (state) => {
      state.disclaimer = { success: [], warning: [], danger: [] };
    },
    clearForm: (state) => {
      return {
        ...initialState,
        isNewDeal: true,
        isCampaignDrawerOpen: state.isCampaignDrawerOpen,
        meta: state.meta,
        campaignType: state.campaignType,
        commonDeal: {
          ...initialState.commonDeal,
          currentLine: {
            ...initialState.commonDeal.currentLine,
            name: state.commonDeal.currentLine.name,
            frontEndType: state.commonDeal.currentLine.frontEndType,
          },
        },
      };
    },
    createNewLine: (state) => {
      state.commonDeal.currentLine = { ...initialState.commonDeal.currentLine };
    },
    createNewLineWithUniqueName: (state, action: PayloadAction<FrontEndType | undefined>) => {
      const lines = state.backupFormData.lines.map((line) => line.name.toLowerCase());
      const idx = lines.length + 1;

      const newLineName = generateUniqueDealLineName(lines, idx);
      const frontEndType = action.payload ?? state.commonDeal.currentLine.frontEndType;
      const isPlanner = frontEndType === FrontEndType.PLANNER;

      state.commonDeal.currentLine = {
        ...initialState.commonDeal.currentLine,
        startDate: isPlanner ? null : new Date(new Date().setHours(0, 0, 0, 0)),
        endDate: isPlanner ? null : new Date(new Date().setHours(23, 59, 59, 999)),
        frontEndType,
        name: newLineName,
      };

      if (state.isCpmCampaignLevel) {
        state.commonDeal.currentLine.cpm = state.commonDeal.cpm ?? 0;
      }
    },
    appendSegment: (state, action: PayloadAction<SelectedSegment>) => {
      state.commonDeal.currentLine.segment.selectedSegments = {
        ...state.commonDeal.currentLine.segment.selectedSegments,
        ...action.payload,
      };
    },
    duplicateLine: (state, action: PayloadAction<string>) => {
      const backupLine = getLineById(state.backupFormData.lines, action.payload);
      state.commonDeal.currentLine = {
        ...state.commonDeal.currentLine,
        ...getDuplicateLineState(backupLine),
        availability: { ...initialState.commonDeal.currentLine.availability },
        preservedAllocation: { ...initialState.commonDeal.currentLine.preservedAllocation },
      };
    },
    hideDisclaimer: (state) => {
      state.isDisclaimerVisible = false;
    },
    updatePlannerSettings: (state, action: PayloadAction<Partial<PlannerSettings>>) => {
      state.plannerSettings = { ...state.plannerSettings, ...action.payload };
    },
    resetPlannerFrameIdIndexes: (state) => {
      state.plannerSettings.frameIdIndexes = [];
      state.plannerSettings.selectedIndexRange = undefined;
    },
    updatePlannerFrameIdIndexes: (state, action: PayloadAction<SegmentHourAverageByFrame>) => {
      const getSegmentFromPayloadMatchingAssetFrameId = (assetFrameId: string): AverageScoresByFrame | undefined =>
        action.payload.avgScoresByFrame.find((payloadSegment) => payloadSegment.frameId === assetFrameId);

      const getIndexScoreOfAsset = (assetFrameId: string): number => {
        const segmentFromPayload = getSegmentFromPayloadMatchingAssetFrameId(assetFrameId);

        if (segmentFromPayload) return Object.values(segmentFromPayload.scores)[0];
        return 0;
      };

      state.plannerSettings.frameIdIndexes = state.commonDeal.currentLine.availability.assets.map((asset) => ({
        frameId: asset.frameId,
        index: getIndexScoreOfAsset(asset.frameId),
      }));
    },
    resetCurrentLineProperty: (state, action: PayloadAction<keyof Line>) => {
      state.commonDeal.currentLine = {
        ...state.commonDeal.currentLine,
        [action.payload]: initialState.commonDeal.currentLine[action.payload],
      };
    },
    resetDealLineAvailability: (state) => {
      state.commonDeal.currentLine.availability = { ...initialState.commonDeal.currentLine.availability };
    },
    resetForm: (state) => {
      const { lines } = state.backupFormData;
      const id = state.commonDeal.currentLine.id || lines[lines.length - 1]?.id;

      state.programmatic = {
        ...state.backupFormData.programmatic,
      };
      state.commonDeal = {
        ...state.backupFormData.commonDeal,
        currentLine: { ...state.commonDeal.currentLine, ...getLineById(lines, id) },
      };
      state.isCpmCampaignLevel = lines.length <= 1 || lines.every((line) => Number(line.cpm) === Number(lines[0].cpm));
    },
    resetProperty: (state, action: PayloadAction<keyof DealManagement>) => {
      return {
        ...state,
        [action.payload]: initialState[action.payload],
      };
    },
    resetSelectedIndexRange: (state) => {
      state.plannerSettings.selectedIndexRange = undefined;
    },
    resetState: (state) => ({
      ...initialState,
      campaignType: state.campaignType,
    }),
    setSelectedEnvironmentChannel: (
      state,
      action: PayloadAction<{
        audienceCategoryGroupCode: string;
        dayPartGroupCode: string;
      }>,
    ) => {
      state.selectedEnvironmentChannel = action.payload;
    },
    showDisclaimer: (state) => {
      state.isDisclaimerVisible = true;
    },
    toggleCampaignDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.plannerSecondaryPanelType = PlannerSecondaryPanelType.NONE;
      state.isCampaignDrawerOpen = action.payload ?? !state.isCampaignDrawerOpen;
      state.campaignViolations = [];
    },
    setCampaignViolations: (state, action: PayloadAction<CampaignViolation[]>) => {
      if (!action.payload.length && !state.campaignViolations.length) return;

      state.campaignViolations = action.payload;
    },
    resetCampaignLevelChanges: (state) => {
      const backup = state.backupFormData.commonDeal;

      state.commonDeal = {
        ...state.commonDeal,
        dealName: backup.dealName,
        advertiser: backup.advertiser,
        brand: backup.brand,
        productCategory: backup.productCategory,
        agency: backup.agency,
        specialist: backup.specialist,
        salesPerson: backup.salesPerson,
        salesTeam: backup.salesTeam,
        adminPerson: backup.adminPerson,
        productCategoryExclusivity: backup.productCategoryExclusivity,
        duplicationExclusion: backup.duplicationExclusion,
        juxtapositionExclusion: backup.juxtapositionExclusion,
        exclusions: backup.exclusions,
        advertiserBlockExclusion: backup.advertiserBlockExclusion,
        agencyBlockExclusion: backup.agencyBlockExclusion,
        specialistBlockExclusion: backup.specialistBlockExclusion,
      };
    },
    requestCampaignNameFocus: (state, action: PayloadAction<boolean>) => {
      state.isCampaignNameFocusRequested = action.payload;
    },
    startLoading: (state, action: PayloadAction<Loading | LocalLoading | undefined>) => {
      if (!state.loading) state.loading = {};

      if (action?.payload) {
        state.loading[action.payload] = true;

        if (Object.values(LocalLoading).includes(action.payload as LocalLoading)) return;
      }

      state.isLoading = true;
    },
    stopLoading: (state, action: PayloadAction<Loading | LocalLoading | undefined>) => {
      if (!action?.payload) {
        state.loading = {};
        state.isLoading = false;

        return;
      }

      if (state.loading[action.payload]) delete state.loading[action.payload];

      state.isLoading = !!Object.values(state.loading).length;
    },
    deleteSegment: (state, action: PayloadAction<string>) => {
      delete state.commonDeal.currentLine.segment.selectedSegments[action.payload];
    },
    removeTemporaryDealId: (state) => {
      state.temporaryDealId = initialState.temporaryDealId;
    },
    changePlannerSecondaryPanelType: (state, action: PayloadAction<PlannerSecondaryPanelType>) => {
      state.plannerSecondaryPanelType = action.payload;
    },
    hidePlannerSecondaryPanel: (state) => {
      state.plannerSecondaryPanelType = PlannerSecondaryPanelType.NONE;
    },
    duplicateDeal: (
      state,
      action: PayloadAction<{
        backupFormData: BackupFormData;
        commonDeal: CommonDeal;
        hasAdsDealLevelCPMEnabled: boolean;
        programmatic: Programmatic;
      }>,
    ) => {
      const {
        bids,
        bookingStatusCode: dealInitialBookingStatusCode,
        dealId,
        internalId,
        overriddenCampaignId,
        summary,
      } = initialState.commonDeal;

      const {
        availability,
        bookingStatusCode,
        createdDate,
        currentRemainingImpressions,
        deliveredImpressions,
        dischargedFrameIds,
        earliestFrameStartDate,
        earliestFrameTimezone,
        endDate,
        id,
        isCurrentLineWithProposalAllocation,
        latestFrameEndDate,
        latestFrameTimezone,
        lineId,
        patternLength,
        patternRepeatType,
        preservedAllocation,
        preservedAvailability,
        proximity,
        routeFrameCodes,
        segment,
        selectedDays,
        startDate,
        terminated,
        venueTaxonomies,
      } = initialState.commonDeal.currentLine;

      const cpm = action.payload.hasAdsDealLevelCPMEnabled
        ? action.payload.commonDeal.currentLine.cpm
        : action.payload.commonDeal.cpm || '';

      const lineEndDate = new Date(action.payload.commonDeal.currentLine.endDate ?? '');
      const lineCreationDate = new Date(createdDate ?? '');

      const duplicatedLine = {
        ...action.payload.commonDeal.currentLine,
        availability,
        bookingStatusCode,
        createdDate,
        currentRemainingImpressions,
        deliveredImpressions,
        dischargedFrameIds,
        earliestFrameStartDate,
        earliestFrameTimezone,
        endDate: isAfter(lineEndDate, lineCreationDate) ? action.payload.commonDeal.currentLine.endDate : endDate,
        id,
        isCurrentLineWithProposalAllocation,
        latestFrameEndDate,
        latestFrameTimezone,
        lineId,
        multiTargetingFile: null,
        name: `${action.payload.commonDeal.currentLine.name} - copy`,
        patternLength: action.payload.commonDeal.currentLine.patternLength || patternLength,
        patternRepeatType: action.payload.commonDeal.currentLine.patternRepeatType || patternRepeatType,
        preservedAllocation,
        preservedAvailability,
        proximity: action.payload.commonDeal.currentLine.proximity || proximity,
        routeFrameCodes: action.payload.commonDeal.currentLine.routeFrameCodes || routeFrameCodes,
        segment: action.payload.commonDeal.currentLine.segment || segment,
        selectedDays: action.payload.commonDeal.currentLine.selectedDays || selectedDays,
        startDate,
        terminated,
        venueTaxonomies: action.payload.commonDeal.currentLine.venueTaxonomies || venueTaxonomies,
      };

      const duplicatedDeal = {
        ...action.payload.commonDeal,
        bids,
        bookingStatusCode: dealInitialBookingStatusCode,
        cpm,
        dealId,
        dealName: `${action.payload.commonDeal.dealName} - copy`,
        internalId,
        overriddenCampaignId,
        summary,
        currentLine: duplicatedLine,
      };

      const { programmatic } = action.payload;

      state.isDuplicated = true;
      state.isNewDeal = true;
      state.programmatic = programmatic;
      state.commonDeal = duplicatedDeal;
      state.backupFormData = {
        commonDeal: duplicatedDeal,
        programmatic,
        lines: [duplicatedLine],
      };
    },
    addDirectSalesLineId: (state, action: PayloadAction<string>) => {
      state.commonDeal.currentLine.lineId = action.payload;
      state.backupFormData.lines[state.backupFormData.lines.length - 1].lineId = action.payload;
    },
    addDirectSalesObjective: (
      state,
      action: PayloadAction<{ objective: DirectSalesLineObjective; lineId: string }>,
    ) => {
      const { objective: newObjective, lineId } = action.payload;

      const idx = state.backupFormData.lines.findIndex((line) => line.lineId === lineId);

      if (idx > -1) {
        const line = state.backupFormData.lines[idx] as DirectSalesLine;
        const currentObjectives = line.objectives ?? [];
        const newObjectives = [...currentObjectives, newObjective];

        state.backupFormData.lines[idx] = {
          ...line,
          objectives: newObjectives,
        };
        state.directSales = { ...state.directSales, objectives: newObjectives };
      }
    },
    changeActiveDirectSalesLine: (state, action: PayloadAction<string>) => {
      const lineId = action.payload;
      const idx = state.backupFormData.lines.findIndex((line) => line.lineId === lineId);

      if (idx >= 0) {
        const activeLine = state.backupFormData.lines[idx] as DirectSalesLine;

        const {
          dealLineFormats,
          mediaType,
          pricingMode,
          locations,
          poi,
          poiPoints,
          state: lineState,
          lockState,
          allocation,
          budgetCriteria,
          framesCriteria,
          impressionsCriteria,
          networkCriteria,
          objectives,
          productTemplate,
          floatingRange,
          sotObjectiveId,
          ...currentLine
        } = activeLine;

        const directSales = {
          state: lineState || initialState.directSales.state,
          lockState: lockState || initialState.directSales.lockState,
          dealLineFormats: dealLineFormats || [],
          locations: locations || [],
          poi: poi || [],
          poiPoints: poiPoints || state.directSales.poiPoints || [],
          budgetCriteria: budgetCriteria || DEFAULT_CRITERIA,
          framesCriteria: framesCriteria || DEFAULT_CRITERIA,
          impressionsCriteria: impressionsCriteria || DEFAULT_CRITERIA,
          networkCriteria: networkCriteria || [],
          mediaType: mediaType || initialState.directSales.mediaType,
          pricingMode: resolveDirectSalesPricingModeForApi({
            pricingMode,
            networkCriteria: networkCriteria || [],
            mediaType,
          }),
          allocation: allocation || initialState.directSales.allocation,
          objectives: objectives || initialState.directSales.objectives,
          transientState: state.directSales.transientState || initialState.directSales.transientState,
          transientLineStates: state.directSales.transientLineStates || initialState.directSales.transientLineStates,
          productTemplate: productTemplate || initialState.directSales.productTemplate,
          floatingRange: floatingRange || initialState.directSales.floatingRange,
          sotObjectiveId: sotObjectiveId ?? initialState.directSales.sotObjectiveId,
          enabledForSharing: state.directSales.enabledForSharing ?? initialState.directSales.enabledForSharing,
          campaignPlanShareUrl: state.directSales.campaignPlanShareUrl ?? initialState.directSales.campaignPlanShareUrl,
          campaignPlanShareExpiresAt:
            state.directSales.campaignPlanShareExpiresAt ?? initialState.directSales.campaignPlanShareExpiresAt,
          isGenerateCampaignPlanEnabled:
            state.directSales.isGenerateCampaignPlanEnabled ?? initialState.directSales.isGenerateCampaignPlanEnabled,
          updatedGridNegotiationTypes:
            state.directSales.updatedGridNegotiationTypes || initialState.directSales.updatedGridNegotiationTypes,
        };

        state.commonDeal.currentLine = currentLine;
        state.isOOCFramesModalDismissed = false;
        state.directSales = directSales;
      }
    },
    clearCampaignPlanShareUrl: (state) => {
      state.directSales.campaignPlanShareUrl = initialState.directSales.campaignPlanShareUrl;
      state.directSales.campaignPlanShareExpiresAt = initialState.directSales.campaignPlanShareExpiresAt;
    },
    setDirectSalesAssetList: (
      state,
      action: PayloadAction<{
        campaign: {
          framesCount: number;
          impressionsCount: number;
        };
        lines: Record<string, DirectSalesAssetLine>;
      }>,
    ) => {
      state.backupFormData.lines.forEach((line: DirectSalesLine) => {
        const { framesCount, impressionsCount, assets, missingAssets } = action.payload.lines[line.lineId] || {
          framesCount: 0,
          impressionsCount: 0,
          assets: [],
          missingAssets: [],
        };

        line.availability.allocatedFrames = framesCount;
        line.availability.allocatedImpressions = impressionsCount;
        line.availability.assets = assets;
        line.availability.missingAssets = missingAssets;
        line.availability.unavailableAssets = [];
      });

      const { framesCount, impressionsCount, assets, missingAssets } = action.payload.lines[
        state.commonDeal.currentLine.lineId
      ] || {
        framesCount: 0,
        impressionsCount: 0,
        assets: [],
        missingAssets: [],
      };
      state.commonDeal.currentLine.availability.allocatedFrames = framesCount;
      state.commonDeal.currentLine.availability.allocatedImpressions = impressionsCount;
      state.commonDeal.currentLine.availability.assets = assets;
      state.commonDeal.currentLine.availability.missingAssets = missingAssets;
      state.commonDeal.currentLine.availability.unavailableAssets = [];

      state.commonDeal.summary.availability.allocatedFrames = action.payload.campaign.framesCount;
      state.commonDeal.summary.availability.allocatedImpressions = action.payload.campaign.impressionsCount;
    },
    clearAssets: (state) => {
      state.backupFormData.lines.forEach((line: DirectSalesLine) => {
        line.availability.allocatedFrames = 0;
        line.availability.allocatedImpressions = 0;
        line.availability.availableFrames = 0;
        line.availability.availableImpressions = 0;
        line.availability.assets = [];
        line.availability.availableNetworkFrames = undefined;
      });

      state.commonDeal.currentLine.availability.allocatedFrames = 0;
      state.commonDeal.currentLine.availability.allocatedImpressions = 0;
      state.commonDeal.currentLine.availability.availableFrames = 0;
      state.commonDeal.currentLine.availability.availableImpressions = 0;
      state.commonDeal.currentLine.availability.assets = [];
      state.commonDeal.currentLine.availability.availableNetworkFrames = undefined;
    },
    clearLineAssets: (state, action: PayloadAction<string>) => {
      const lineId = action.payload;
      const lineIndex = state.backupFormData.lines.findIndex((line) => line.lineId === lineId);

      if (lineIndex > -1) {
        const { availability } = state.backupFormData.lines[lineIndex];

        availability.allocatedFrames = 0;
        availability.allocatedImpressions = 0;
        availability.availableFrames = 0;
        availability.availableImpressions = 0;
        availability.totalCost = 0;
        availability.assets = [];
        availability.availableNetworkFrames = undefined;
      }

      if (state.commonDeal.currentLine.lineId === lineId) {
        state.commonDeal.currentLine.availability.allocatedFrames = 0;
        state.commonDeal.currentLine.availability.allocatedImpressions = 0;
        state.commonDeal.currentLine.availability.availableFrames = 0;
        state.commonDeal.currentLine.availability.availableImpressions = 0;
        state.commonDeal.currentLine.availability.totalCost = 0;
        state.commonDeal.currentLine.availability.assets = [];
        state.commonDeal.currentLine.availability.availableNetworkFrames = undefined;
      }
    },
    clearDirectSalesAssetList: (state) => {
      state.backupFormData.lines.forEach((line: DirectSalesLine) => {
        line.availability.allocatedFrames = initialState.commonDeal.currentLine.availability.allocatedFrames;
        line.availability.allocatedImpressions = initialState.commonDeal.currentLine.availability.allocatedImpressions;
      });

      state.commonDeal.currentLine.availability.allocatedFrames =
        initialState.commonDeal.currentLine.availability.allocatedFrames;
      state.commonDeal.currentLine.availability.allocatedImpressions =
        initialState.commonDeal.currentLine.availability.allocatedImpressions;
      state.commonDeal.summary.availability.allocatedFrames =
        initialState.commonDeal.summary.availability.allocatedFrames;
      state.commonDeal.summary.availability.allocatedImpressions =
        initialState.commonDeal.summary.availability.allocatedImpressions;
    },
    setDirectSalesPrices: (
      state,
      action: PayloadAction<{ campaignPrice: number; linePrices: Record<string, number> }>,
    ) => {
      state.commonDeal.summary.availability.totalCost = action.payload.campaignPrice;
      state.backupFormData.commonDeal.summary.availability.totalCost = action.payload.campaignPrice;

      state.backupFormData.lines.forEach((line: DirectSalesLine) => {
        const linePrice = action.payload.linePrices[line.lineId];

        line.availability.totalCost = linePrice;

        if (line.lineId === state.commonDeal.currentLine.lineId) {
          state.commonDeal.currentLine.availability.totalCost = linePrice;
        }
      });
    },
    clearDirectSalesPrices: (state) => {
      state.commonDeal.summary.availability.totalCost = 0;
      state.backupFormData.commonDeal.summary.availability.totalCost = 0;

      state.backupFormData.lines.forEach((line: DirectSalesLine) => {
        line.availability.totalCost = 0;
      });

      state.commonDeal.currentLine.availability.totalCost = 0;
    },
    updateDirectSalesLineLockState: (state, action: PayloadAction<DirectSalesLockStatusOption>) => {
      state.directSales.lockState.status = action.payload;
    },
    changeDirectSalesData: (state, action: PayloadAction<Partial<DirectSales>>) => {
      state.directSales = { ...state.directSales, ...action.payload };
    },

    changeDirectSalesLineName: (state, action: PayloadAction<string>) => {
      const idx = state.backupFormData.lines.findIndex((line) => line.lineId === state.commonDeal.currentLine.lineId);

      if (idx > -1) {
        state.backupFormData.lines[idx].name = action.payload;
        state.commonDeal.currentLine.name = action.payload;
      }
    },
    clearDirectSalesAllocation: (state) => {
      state.backupFormData.lines.forEach((line: DirectSalesLine) => {
        line.allocation = initialState.directSales.allocation;
        line.state = { status: DirectSalesStatusOption.DRAFT_UPDATING };
        line.objectives.forEach((objective) => {
          objective.objectiveStatus = undefined;
        });
      });

      state.directSales.allocation = initialState.directSales.allocation;
      state.directSales.state = { status: DirectSalesStatusOption.DRAFT_UPDATING };
      state.directSales.objectives.forEach((objective) => {
        objective.objectiveStatus = undefined;
      });
    },
    changeCampaignPlannerMode: (state, action: PayloadAction<CampaignPlannerModeType>) => {
      state.campaignPlannerMode = action.payload;
      state.plannerSecondaryPanelType = PlannerSecondaryPanelType.NONE;
    },
    removeDirectSalesLine: (state) => {
      const { lineId } = state.commonDeal.currentLine;

      const idx = state.backupFormData.lines.findIndex((line) => line.lineId === lineId);
      const lines = state.backupFormData.lines.filter((line) => line.lineId !== lineId) as DirectSalesLine[];
      const newLineIdx = idx > -1 && idx === state.backupFormData.lines.length - 1 ? lines.length - 1 : idx;

      const newActiveLine = lines[newLineIdx] || initialState.commonDeal.currentLine;

      state.commonDeal.currentLine = newActiveLine;
      state.isOOCFramesModalDismissed = false;
      state.directSales = {
        state: newActiveLine.state,
        lockState: newActiveLine.lockState,
        dealLineFormats: newActiveLine.dealLineFormats,
        locations: newActiveLine.locations,
        poi: newActiveLine.poi,
        poiPoints: state.directSales.poiPoints || [],
        mediaType: newActiveLine.mediaType,
        pricingMode: resolveDirectSalesPricingModeForApi({
          pricingMode: newActiveLine.pricingMode,
          networkCriteria: newActiveLine.networkCriteria,
          mediaType: newActiveLine.mediaType,
        }),
        allocation: newActiveLine.allocation,
        budgetCriteria: newActiveLine.budgetCriteria,
        framesCriteria: newActiveLine.framesCriteria,
        impressionsCriteria: newActiveLine.impressionsCriteria,
        networkCriteria: newActiveLine.networkCriteria,
        objectives: newActiveLine.objectives,
        productTemplate: newActiveLine.productTemplate,
        floatingRange: newActiveLine.floatingRange,
        transientState: state.directSales.transientState ?? initialState.directSales.transientState,
        transientLineStates: state.directSales.transientLineStates ?? initialState.directSales.transientLineStates,
        updatedGridNegotiationTypes:
          state.directSales.updatedGridNegotiationTypes ?? initialState.directSales.updatedGridNegotiationTypes,
      };
      state.backupFormData.lines = lines;
    },
    setLoadedDirectSalesData: (
      state,
      action: PayloadAction<{
        commonDeal: Partial<CommonDeal>;
        lines: Partial<DirectSalesLine>[];
        campaignConfiguration: PublisherCampaignConfiguration;
      }>,
    ) => {
      const { commonDeal, lines, campaignConfiguration } = action.payload;

      const currentLine =
        lines.find((line) => line.lineId === state.commonDeal.currentLine.lineId) || lines[lines.length - 1];

      const resolvePricingMode = (line: Partial<DirectSalesLine>): DirectSalesPricingMode =>
        resolveDirectSalesLinePricingMode(line, campaignConfiguration);

      const directSales = {
        state: currentLine.state || initialState.directSales.state,
        lockState: currentLine.lockState || initialState.directSales.lockState,
        dealLineFormats: currentLine.dealLineFormats || [],
        locations: currentLine.locations || [],
        poi: currentLine.poi || [],
        poiPoints: state.directSales.poiPoints || [],
        budgetCriteria: currentLine.budgetCriteria || initialState.directSales.budgetCriteria,
        framesCriteria: currentLine.framesCriteria || initialState.directSales.framesCriteria,
        impressionsCriteria: currentLine.impressionsCriteria || initialState.directSales.impressionsCriteria,
        networkCriteria: currentLine.networkCriteria || initialState.directSales.networkCriteria,
        mediaType: currentLine.mediaType || initialState.directSales.mediaType,
        pricingMode: resolvePricingMode(currentLine),
        periodType: currentLine.periodType || initialState.directSales.periodType,
        weekStartDate: currentLine.weekStartDate || initialState.directSales.weekStartDate,
        allocation: currentLine.allocation || initialState.directSales.allocation,
        objectives: currentLine.objectives || initialState.directSales.objectives,
        transientState: state.directSales.transientState || initialState.directSales.transientState,
        transientLineStates: state.directSales.transientLineStates || initialState.directSales.transientLineStates,
        productTemplate: currentLine.productTemplate || initialState.directSales.productTemplate,
        floatingRange: currentLine.floatingRange || null,
        sotObjectiveId: currentLine.sotObjectiveId ?? initialState.directSales.sotObjectiveId,
        enabledForSharing: currentLine.enabledForSharing,
        campaignPlanShareUrl: state.directSales.campaignPlanShareUrl ?? initialState.directSales.campaignPlanShareUrl,
        campaignPlanShareExpiresAt:
          state.directSales.campaignPlanShareExpiresAt ?? initialState.directSales.campaignPlanShareExpiresAt,
        isGenerateCampaignPlanEnabled:
          state.directSales.isGenerateCampaignPlanEnabled ?? initialState.directSales.isGenerateCampaignPlanEnabled,
        updatedGridNegotiationTypes:
          state.directSales.updatedGridNegotiationTypes || initialState.directSales.updatedGridNegotiationTypes,
        allocationFailures: currentLine.allocationFailures || initialState.directSales.allocationFailures,
      };

      state.backupFormData = {
        commonDeal: { ...initialState.backupFormData.commonDeal, ...initialState.commonDeal, ...commonDeal },
        lines: lines.map((line) => ({
          ...initialState.commonDeal.currentLine,
          ...line,
          pricingMode: resolvePricingMode(line),
          budgetCriteria: line.budgetCriteria || initialState.directSales.budgetCriteria,
          framesCriteria: line.framesCriteria || initialState.directSales.framesCriteria,
          impressionsCriteria: line.impressionsCriteria || initialState.directSales.impressionsCriteria,
          frontEndType: FrontEndType.PLANNER,
          availability: { ...initialState.commonDeal.currentLine.availability, ...line.availability },
        })),
        programmatic: initialState.backupFormData.programmatic,
      };

      state.directSales = directSales;
      state.commonDeal = {
        ...initialState.commonDeal,
        ...commonDeal,
        currentLine: {
          ...initialState.commonDeal.currentLine,
          ...currentLine,
          frontEndType: FrontEndType.PLANNER,
          availability: {
            ...initialState.commonDeal.currentLine.availability,
            ...state.commonDeal.currentLine.availability,
          },
        },
      };
      state.isNewDeal = false;
    },
    updateDirectSalesSessionLock: (state, action: PayloadAction<DirectSalesLockState>) => {
      state.directSalesSessionLock = action.payload || initialState.directSalesSessionLock;
    },
    updateBackupFormDataLine: (
      state,
      action: PayloadAction<{
        lineId: string;
        newBackupLineData: Partial<DirectSalesLine>;
      }>,
    ) => {
      const idx = state.backupFormData.lines.findIndex((line) => line.lineId === action.payload.lineId);

      if (idx > -1) {
        state.backupFormData.lines[idx] = { ...state.backupFormData.lines[idx], ...action.payload.newBackupLineData };
      }
    },
    updateBackupFormDataCommonDeal: (state, action: PayloadAction<Partial<CommonDeal>>) => {
      state.backupFormData.commonDeal = { ...state.backupFormData.commonDeal, ...action.payload };
    },
    changeAssetsView: (state, action: PayloadAction<AssetsView>) => {
      state.plannerSettings.assetsView = action.payload;
    },
    updateCurrentLineAndBackupLineProperty: (
      state,
      action: PayloadAction<{
        lineId: string;
        type: 'isFetchingAvailability' | 'hasAvailabilityResult' | 'isSaving';
        value: boolean;
      }>,
    ) => {
      const { lineId, type, value: propertyValue } = action.payload;

      const lineIndex = state.backupFormData.lines?.findIndex((line) => line.lineId === lineId);
      const isLineInBackupLines = lineIndex > -1;

      if (!isLineInBackupLines) {
        state.commonDeal.currentLine[type] = propertyValue;
        return;
      }

      state.backupFormData.lines[lineIndex] = {
        ...state.backupFormData.lines[lineIndex],
        [type]: propertyValue,
      };

      const isCurrentLine = state.backupFormData.lines[lineIndex].lineId === state.commonDeal.currentLine.lineId;
      if (isCurrentLine) {
        state.commonDeal.currentLine[type] = propertyValue;
      }
    },
    setDirectSalesStates: (
      state,
      action: PayloadAction<Record<string, { state: DirectSalesState; allocation?: DirectSalesLineAllocation }>>,
    ) => {
      state.backupFormData.lines.forEach((line: DirectSalesLine) => {
        const newLineData = action.payload[line.lineId];

        if (newLineData) {
          line.state = newLineData.state;

          if (newLineData.allocation) line.allocation = newLineData.allocation;

          if (line.lineId === state.commonDeal.currentLine.lineId) {
            state.directSales.state = newLineData.state;

            if (newLineData.allocation) state.directSales.allocation = newLineData.allocation;
          }
        }
      });
    },
    setDirectSalesGrid: (state, action: PayloadAction<DirectSalesGrid>) => {
      state.commonDeal.pricingSolution.grid = action.payload;
      state.backupFormData.commonDeal.pricingSolution.grid = action.payload;
    },
    setDirectSalesUpdatedNegotiationTypes: (
      state,
      action: PayloadAction<DirectSalesUpdatedCampaignNegotiationTypes>,
    ) => {
      const updatedNegotiationRows = action.payload;

      state.directSales.updatedGridNegotiationTypes = updatedNegotiationRows;
    },
    updateDirectSalesBackupFormDataLine: (state) => {
      const idx = state.backupFormData.lines.findIndex((line) => line.lineId === state.commonDeal.currentLine.lineId);

      if (idx > -1) {
        state.backupFormData.lines[idx] = {
          ...state.commonDeal.currentLine,
          ...state.directSales,
          availability: {
            ...state.commonDeal.currentLine.availability,
            assets: [],
            missingAssets: [],
          },
          frontEndType: FrontEndType.PLANNER,
        };
      }
    },
    updateDirectSalesStatus: (state, action: PayloadAction<DirectSalesState>) => {
      const idx = state.backupFormData.lines.findIndex((line) => line.lineId === state.commonDeal.currentLine.lineId);

      if (idx > -1 && !isDirectSalesCampaignCloned(state.commonDeal.dealId)) {
        state.backupFormData.lines[idx] = {
          ...state.backupFormData.lines[idx],
          state: action.payload,
        };
      }

      state.directSales = { ...state.directSales, state: action.payload };
    },
    updateLineImpressionMetrics: (state, action: PayloadAction<ImpressionMetrics>) => {
      state.commonDeal.currentLine.impressionMetrics = action.payload;
      state.backupFormData.commonDeal.currentLine.impressionMetrics = action.payload;
    },
    changeTransientState: (
      state,
      action: PayloadAction<CampaignVersioningEditingStatus | CampaignVersioningErrors | undefined>,
    ) => {
      state.directSales.transientState = action.payload;
    },
    updateAllowedStateTransitionsMetadata: (state, action: PayloadAction<AllowedStateTransitionsMetadata>) => {
      state.meta.allowedStateTransitions = { ...state.meta.allowedStateTransitions, ...action.payload };
    },
    updateOriginalLineStatuses: (state, action: PayloadAction<Record<string, DirectSalesState>>) => {
      state.meta.originalLineStatuses = action.payload;
    },
    updateTransientLineStates: (state, action: PayloadAction<DirectSalesTransientDealLineState>) => {
      state.directSales.transientLineStates = action.payload;
    },
    clearTransientLineStates: (state) => {
      state.directSales.transientState = undefined;
      state.directSales.transientLineStates = {};
      state.meta.originalLineStatuses = {};
    },
    setDirectSalesClusterSettings: (state) => {
      state.plannerSettings.isAllocationClusterVisible = false;
      state.plannerSettings.isAvailabilityClusterVisible = false;
      state.plannerSettings.isImpressionsClusterVisible = false;
    },
    setHoveredFurnitureCode: (state, action: PayloadAction<string>) => {
      state.plannerSettings.hoveredFurnitureCode = action.payload;
    },
    setIsFetchingAdvancedAvailabilityReport: (state, action: PayloadAction<boolean>) => {
      state.isFetchingAdvancedAvailabilityReport = action.payload;
    },
    setIsFetchingProximityReport: (state, action: PayloadAction<boolean>) => {
      state.isFetchingProximityReport = action.payload;
    },
  },
});

export const {
  clearCampaignPlanShareUrl,
  addDealLineVenueTaxonomy,
  changeCurrentLine,
  changeCurrentLineToLastLine,
  dismissOOCFramesModal,
  changeDealCurrentLineData,
  changeDealCurrentLineAvailabilityAssetsSot,
  changeDealData,
  changeDealDetails,
  changeCurrentDealLineAvailability,
  changeBackupDealLineAvailabilityWithoutAssets,
  changeDealLineAllocatedImpressions,
  changeDealLineVenueTaxonomy,
  changeOnlyProgrammaticDealData,
  changeSegment,
  changeCampaignType,
  changeTransientState,
  changeSegmentFrameAverageByHour,
  clearSegmentFrameAverageByHour,
  changeDisclaimer,
  changeFormParams,
  changeFrontEndType,
  changeIsCpmCampaignLevel,
  changeCurrentLineAvailability,
  changeIsNewDeal,
  changeIsNewLineInEndedDeal,
  changeMeta,
  updatePlannerSettings,
  updatePlannerFrameIdIndexes,
  resetPlannerFrameIdIndexes,
  resetSelectedIndexRange,
  clearAllocationInputs,
  clearDealLineVenueTaxonomies,
  clearSegment,
  clearDisclaimer,
  clearForm,
  createNewLine,
  createNewLineWithUniqueName,
  duplicateDeal,
  duplicateLine,
  appendSegment,
  hideDisclaimer,
  removeDealLineVenueTaxonomy,
  resetDealLineAvailability,
  resetForm,
  resetProperty,
  resetState,
  setSelectedEnvironmentChannel,
  showDisclaimer,
  startLoading,
  stopLoading,
  toggleCampaignDrawerOpen,
  setCampaignViolations,
  resetCampaignLevelChanges,
  requestCampaignNameFocus,
  changeCampaignPlannerMode,
  deleteSegment,
  changePlannerSecondaryPanelType,
  hidePlannerSecondaryPanel,
  removeTemporaryDealId,
  addDirectSalesLineId,
  addDirectSalesObjective,
  changeActiveDirectSalesLine,
  setDirectSalesAssetList,
  clearDirectSalesAssetList,
  setDirectSalesPrices,
  clearDirectSalesPrices,
  clearAssets,
  clearLineAssets,
  changeDirectSalesData,
  changeDirectSalesLineName,
  clearDirectSalesAllocation,
  removeDirectSalesLine,
  setLoadedDirectSalesData,
  updateBackupFormDataLine,
  updateBackupFormDataCommonDeal,
  updateCurrentLineAndBackupLineProperty,
  changeAssetsView,
  setDirectSalesStates,
  setDirectSalesGrid,
  updateDirectSalesBackupFormDataLine,
  updateDirectSalesStatus,
  updateLineImpressionMetrics,
  updateDirectSalesSessionLock,
  updateAllowedStateTransitionsMetadata,
  updateOriginalLineStatuses,
  updateTransientLineStates,
  updatePlannerIndexOptimisation,
  clearTransientLineStates,
  updateDirectSalesLineLockState,
  setDirectSalesUpdatedNegotiationTypes,
  changeDirectSalesNegotiationFailedValues,
  setDirectSalesClusterSettings,
  setHoveredFurnitureCode,
  changeCurrentLineDistributionOfAvailability,
  setIsFetchingAdvancedAvailabilityReport,
  setIsFetchingProximityReport,
} = dealManagementSlice.actions;

export default dealManagementSlice.reducer;

/* eslint-enable sonarjs/cognitive-complexity */
